import { PanelLeft } from "lucide-react";
import { type FC, useEffect, useState } from "react";

import { HeaderIconButton } from "@/components/app-header";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverTitle
} from "@/components/ui/popover";

import { ideLayoutStore, useIdeLayout } from "../layout/ideLayoutStore";

/** How long the "files hidden" note stays up before leaving just the dot. */
export const AUTO_HIDDEN_NOTE_MS = 5000;

/**
 * The header's files toggle. When the editor hid the files rail to make room
 * for its review rail, a dot stays on the toggle until the files are shown
 * again, and a note says so once.
 */
export const FilesToggle: FC = () => {
  const ide = useIdeLayout();
  const autoHidden = ide.autoHidden.left;
  const [noteOpen, setNoteOpen] = useState(false);

  useEffect(() => {
    setNoteOpen(autoHidden);
    if (!autoHidden) return undefined;
    const timer = window.setTimeout(
      () => setNoteOpen(false),
      AUTO_HIDDEN_NOTE_MS
    );
    return () => window.clearTimeout(timer);
  }, [autoHidden]);

  return (
    <Popover open={noteOpen} onOpenChange={setNoteOpen}>
      <PopoverAnchor asChild>
        <span className="relative inline-flex">
          <HeaderIconButton
            label={
              autoHidden
                ? "Show files (hidden to make room for comments)"
                : ide.collapsed.left
                ? "Show files"
                : "Hide files"
            }
            pressed={!ide.collapsed.left}
            onClick={() => ideLayoutStore.toggle("left")}
          >
            <PanelLeft className="size-4" />
          </HeaderIconButton>
          {autoHidden ? (
            <span
              aria-hidden
              data-testid="files-hidden-dot"
              className="pointer-events-none absolute top-0.5 right-0.5 size-2 rounded-full bg-primary ring-cutout ring-background"
            />
          ) : null}
        </span>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-64"
        // Opened by the editor, not by a click here: keep the caret where it is.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <PopoverTitle>Files hidden to make room for comments</PopoverTitle>
        <PopoverDescription>
          Click the files button to show them again.
        </PopoverDescription>
      </PopoverContent>
    </Popover>
  );
};
